import { SerializedError, createSlice, isAnyOf } from '@reduxjs/toolkit';

import { REQUEST_METHODS } from '@lib/core/service/consts';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { parseError } from '@lib/core/service/requests/parseError';
import { request } from '@lib/core/service/requests/request';
import { selectWishlistProductInstanceIds, selectWishlistProductListData } from '@lib/core/users/selectors/productList';
import {
  productListApiUrlCreator,
  productListProductApiUrlCreator,
  updateProductListProductApiUrlCreator,
} from '@lib/core/users/slices/urls';
import {
  IGetProductListParams,
  IProductList,
  IProductListResponse,
  IUpdateProductListParams,
} from '@lib/core/users/types';
import { PRODUCT_LIST_WISHLIST } from '@lib/tools/shared/helpers/consts';

export interface IProductListState {
  data: IProductList[][];
  error: SerializedError;
  isLoading: boolean;
}

export const initialState: IProductListState = {
  data: [],
  error: null,
  isLoading: false,
};

export const actionGetProductList = createTypedAsyncThunk<IProductListResponse, IGetProductListParams>(
  'users/productList/actionGetProductList',
  async ({ listName }) => {
    try {
      const allUserProductLists = await request({ url: productListApiUrlCreator() });
      const productList = allUserProductLists.results.find(list => list?.list_name === listName);

      if (!productList) {
        return { listData: null, listName };
      }

      const fetchProducts = async (url: string | null, accumulated: IProductList[] = []): Promise<IProductList[]> => {
        if (!url) return accumulated;
        const { results, next } = await request({ url });
        return fetchProducts(next, [...accumulated, ...results]);
      };

      const listData = await fetchProducts(productListProductApiUrlCreator(productList.list_slug));

      return { listData, listName };
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

export const actionUpdateProductList = createTypedAsyncThunk<IProductListResponse, IUpdateProductListParams>(
  'users/productList/actionUpdateProductList',
  async ({ productId, listName }, { getState }) => {
    const state = getState();
    const listData = {
      [PRODUCT_LIST_WISHLIST]: {
        data: selectWishlistProductListData(state),
        productIds: selectWishlistProductInstanceIds(state),
      },
    };

    const loadedListData = listData[listName].data.find(data => data.list_name === listName);

    const updateList = async (data: IProductList) => {
      const listSlug = data.list_slug || '';
      const isProductInList = listData[listName].productIds.includes(productId);

      const url = isProductInList
        ? updateProductListProductApiUrlCreator(listSlug, productId)
        : productListApiUrlCreator();
      const method = isProductInList ? REQUEST_METHODS.DELETE : REQUEST_METHODS.POST;
      const body = { list_name: listName, list_slug: listSlug, product: productId };

      return await request({ body, method, url }).then(() =>
        request({ url: productListProductApiUrlCreator(listSlug) }),
      );
    };

    const createList = async () =>
      await request({
        body: { list_name: listName, product: productId },
        method: REQUEST_METHODS.POST,
        url: productListApiUrlCreator(),
      }).then(data => request({ url: productListProductApiUrlCreator(data.list_slug) }));

    try {
      let response;

      if (loadedListData) {
        response = await updateList(loadedListData);
      } else {
        const allUserProductLists = await request({ url: productListApiUrlCreator() });
        const createdListData: IProductList = allUserProductLists.results.find(list => list?.list_name === listName);

        if (!createdListData) {
          response = await createList();
        } else {
          response = await updateList(createdListData);
        }
      }

      return { listData: response.results, listName };
    } catch (error) {
      return Promise.reject(parseError(error));
    }
  },
);

const productListSlice = createSlice({
  extraReducers: builder => {
    builder.addMatcher(isAnyOf(actionGetProductList.pending, actionUpdateProductList.pending), state => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addMatcher(
      isAnyOf(actionGetProductList.fulfilled, actionUpdateProductList.fulfilled),
      (state, { payload }) => {
        state.isLoading = false;

        const { listData, listName } = payload;
        const isListExist = !!state.data.find(list => list?.length && list[0].list_name === listName);

        if (!isListExist && listData) {
          // add new list
          state.data.push(listData);
        } else if (isListExist) {
          // update lists and remove empty lists
          state.data = state.data
            .map(list => (list?.length > 0 && list[0].list_name === listName ? listData : list))
            .filter(list => list?.length);
        }
      },
    );
    builder.addMatcher(isAnyOf(actionGetProductList.rejected, actionUpdateProductList.rejected), (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
  },
  initialState,
  name: 'productList',
  reducers: {
    actionResetProductList: () => initialState,
  },
});

export const { actionResetProductList } = productListSlice.actions;
export default productListSlice.reducer;
